import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FlatList, Pressable, StyleSheet, Text, View } from 'react-native';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Feather from '@expo/vector-icons/Feather';
import { LinearGradient } from 'expo-linear-gradient';
import { Paprika, PaprikaDietWash, PaprikaGradients } from '../../../styles/theme';
import { OmniBar } from '../../recipes/OmniBar';
import { SearchShortcutChips, type ShortcutChipId } from '../../recipes/SearchShortcutChips';
import { AiToken } from '../../recipes/AiToken';
import { UnderstoodLine } from '../../recipes/UnderstoodLine';
import { RemoteImage } from '../../shared/RemoteImage';
import { RecipePlaceholder } from '../../shared/RecipePlaceholder';
import { TimeChip } from './primitives/TimeChip';
import { useRecipeSearch, liveSearch } from '../../../hooks/useRecipeSearch';
import { useCookPreferences } from '../../../hooks/useCookPreferences';
import { selectActivePantryNames } from '../../../redux/inventorySlice';
import { resolveRecipeTitle } from '../../../utils/recipeTitle';
import { trackEvent } from '../../../services/analyticsService';
import type { RecipeIndexEntry } from '../../../services/unifiedRecipeService';
import type { PersonalRecipeIndexEntry } from '../../../services/personalRecipeIndex';
import { dietKindOf } from './dietKind';
import { MEALS } from './constants';
import {
  buildPickerPool,
  buildIdleItems,
  buildResultItems,
  type PickerItem,
  type PickerSectionId,
} from './libraryPicker';

interface Props {
  entries: RecipeIndexEntry[];
  /** Row of the slot being filled (0 = breakfast), or null when no slot is targeted. */
  mealIndex: number | null;
  /** "Mettre mercredi" — what the hovered row's button says. Null: the + alone. */
  placeLabel: string | null;
  /** Desktop focuses the field on open; a phone would cover the list with its keyboard. */
  autoFocus: boolean;
  onSelect: (recipeId: string) => void;
  /** Hovered row, so the grid can preview it in the target slot. Desktop only. */
  onPreview?: (recipeId: string | null) => void;
}

const LIST_WINDOWING = {
  initialNumToRender: 14,
  maxToRenderPerBatch: 14,
  windowSize: 5,
} as const;

const SECTION_LABEL: Record<PickerSectionId, string> = {
  ingredients: 'thisWeek.picker.withIngredients',
  library: 'thisWeek.wholeLibrary',
  results: 'thisWeek.picker.results',
  similar: 'recipes.nearMissEyebrow',
};

/**
 * The planner's Library: the Recipes tab's search, choosing a dish for a slot.
 *
 * Same hook, same bar, same chips and tokens as the Recipes tab, so a query
 * finds the same recipes in both places. Only the result row differs: it
 * places the dish instead of opening it. Wrapped by `LibraryDrawer` (desktop)
 * and `LibrarySheet` (phone), which own the header and the slot context.
 */
export function LibraryPickerPanel({ entries, mealIndex, placeLabel, autoFocus, onSelect, onPreview }: Props) {
  const { t, i18n } = useTranslation();
  const prefs = useCookPreferences();
  const pantryNames = useSelector(selectActivePantryNames);

  // A breakfast slot opens with its filter applied, shown as a token the cook
  // can remove — not a hidden rule, as the old drawer had.
  const [breakfastOnly, setBreakfastOnly] = useState(mealIndex === 0);
  const [rawQuery, setRawQuery] = useState('');
  const [submittedQuery, setSubmittedQuery] = useState('');
  const [inputFocused, setInputFocused] = useState(autoFocus);
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  const pool = useMemo(
    () => buildPickerPool(entries, {
      breakfastOnly,
      isExcluded: prefs.hasHardRules ? (r) => prefs.isExcluded(r) : undefined,
    }),
    [entries, breakfastOnly, prefs],
  );
  const search = useRecipeSearch(pool, pantryNames);

  // Live results while typing, token results once submitted — as on the Recipes tab.
  const isEditingNewQuery = rawQuery.trim().length > 0 && rawQuery !== submittedQuery;
  const liveResults = useMemo(
    () => (isEditingNewQuery ? liveSearch(rawQuery, search.searchDocs) : null),
    [isEditingNewQuery, rawQuery, search.searchDocs],
  );
  const usingLive = !!liveResults && liveResults.length > 0;
  const searching = rawQuery.trim().length > 0 || search.tokens.length > 0 || search.isExactSearch;

  const items = useMemo<PickerItem[]>(() => {
    if (!searching) return buildIdleItems(pool, pantryNames);
    if (usingLive) return buildResultItems(liveResults!, false);
    // Typed, not yet submitted, and the live pass found nothing.
    if (isEditingNewQuery && search.tokens.length === 0) return buildResultItems([], false);
    return buildResultItems(search.results, search.hasNoMatch);
  }, [searching, usingLive, liveResults, isEditingNewQuery, pool, pantryNames, search.results, search.hasNoMatch, search.tokens.length]);

  const listRef = useRef<FlatList<PickerItem>>(null);
  useEffect(() => {
    listRef.current?.scrollToOffset({ offset: 0, animated: false });
  }, [searching, submittedQuery, breakfastOnly]);

  // A search closed without a pick is the Recipes tab's "abandoned" signal too.
  const abandonRef = useRef(search.signalAbandoned);
  abandonRef.current = search.signalAbandoned;
  useEffect(() => () => abandonRef.current(), []);

  // Moving from one row to the next can deliver the new row's hover-in before
  // the old row's hover-out, so a leave only clears the row it belongs to.
  const hoveredRef = useRef<string | null>(null);
  const setHover = (id: string | null) => {
    hoveredRef.current = id;
    setHoveredId(id);
    onPreview?.(id);
  };
  const leave = (id: string) => {
    if (hoveredRef.current === id) setHover(null);
  };

  const handleChangeText = (text: string) => {
    setRawQuery(text);
    // Clearing the bar goes back to the list, not to the last search's results.
    if (!text.trim()) {
      setSubmittedQuery('');
      search.reset();
    }
  };

  const handleSubmit = () => {
    if (!rawQuery.trim()) return;
    search.submitQuery(rawQuery);
    setSubmittedQuery(rawQuery);
  };

  const handlePick = (recipe: PersonalRecipeIndexEntry, section: PickerSectionId) => {
    if (searching) search.signalResultClicked();
    trackEvent('planner_library_pick', {
      section,
      searched: searching,
      query_length: rawQuery.trim().length,
      tokens: search.tokens.map((tok) => tok.id),
      breakfast_filter: breakfastOnly,
    });
    setHover(null);
    onSelect(recipe.id);
  };

  const hasAiToken = search.tokens.some((tok) => tok.source === 'ai');
  const showTokens = breakfastOnly || search.tokens.length > 0;

  const renderItem = ({ item }: { item: PickerItem }) => {
    switch (item.kind) {
      case 'header':
        return (
          <View style={styles.sectionHeader} testID={`library-section-${item.section}`}>
            <Text style={styles.eyebrow}>{t(SECTION_LABEL[item.section])}</Text>
            {item.count != null && (
              <Text style={styles.count} testID={`library-count-${item.section}`}>
                {t('thisWeek.picker.recipeCount', { count: item.count })}
              </Text>
            )}
          </View>
        );
      case 'nearMiss':
        return (
          <View style={styles.banner} testID="library-near-miss">
            <Feather name="info" size={14} color={Paprika.textSecondary} />
            <Text style={styles.bannerText}>{t('recipes.nearMissBanner')}</Text>
          </View>
        );
      case 'empty':
        return (
          <View style={styles.empty} testID="library-empty">
            <Feather name="search" size={22} color={Paprika.textMuted} />
            <Text style={styles.emptyText}>{t('recipes.noResultsAtAll')}</Text>
          </View>
        );
      case 'recipe':
        return (
          <PickerRow
            recipe={item.recipe}
            title={resolveRecipeTitle(item.recipe, i18n.language)}
            hovered={hoveredId === item.recipe.id}
            placeLabel={placeLabel}
            language={i18n.language}
            onPress={() => handlePick(item.recipe, item.section)}
            onHoverIn={() => setHover(item.recipe.id)}
            onHoverOut={() => leave(item.recipe.id)}
          />
        );
    }
  };

  return (
    <View style={styles.root}>
      <View style={styles.searchRow}>
        <OmniBar
          value={rawQuery}
          onChangeText={handleChangeText}
          onFocus={() => setInputFocused(true)}
          onBlur={() => setInputFocused(false)}
          onCancel={() => handleChangeText('')}
          onSubmitEditing={handleSubmit}
          // Stays "open" while it holds a query, so the clear button stays within reach.
          focused={inputFocused || rawQuery.length > 0}
          showCancel={false}
          testID="library-search-input"
        />
      </View>

      {showTokens && (
        <View style={styles.tokensBlock} testID="library-tokens">
          {hasAiToken ? <UnderstoodLine /> : <Text style={styles.eyebrow}>{t('recipes.activeFilters')}</Text>}
          <View style={styles.tokenRow}>
            {breakfastOnly && (
              <AiToken
                label={t(MEALS[0].labelKey)}
                kind="meal"
                source="user"
                onRemove={() => setBreakfastOnly(false)}
              />
            )}
            {search.tokens.map((token) => (
              <AiToken
                key={token.id}
                label={token.labelKey ? t(token.labelKey, token.labelParams) : token.label}
                kind={token.kind}
                neg={token.neg}
                source={token.source}
                onRemove={() => search.removeToken(token.id)}
              />
            ))}
          </View>
        </View>
      )}

      <View style={styles.chips}>
        <SearchShortcutChips
          activeTokenIds={search.tokens.map((tok) => tok.id)}
          onChipPress={(chipId: ShortcutChipId) => search.addShortcutToken(chipId)}
        />
      </View>

      <FlatList
        ref={listRef}
        testID="library-list"
        data={items}
        keyExtractor={(item) => item.key}
        renderItem={renderItem}
        extraData={hoveredId}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
        style={styles.list}
        contentContainerStyle={styles.listContent}
        {...LIST_WINDOWING}
      />
    </View>
  );
}

interface RowProps {
  recipe: PersonalRecipeIndexEntry;
  title: string;
  hovered: boolean;
  placeLabel: string | null;
  language: string;
  onPress: () => void;
  onHoverIn: () => void;
  onHoverOut: () => void;
}

/**
 * One recipe. The whole row places it; nothing inside is a separate Pressable,
 * so react-native-web's hover lock never un-hovers the row under the cursor.
 */
function PickerRow({ recipe, title, hovered, placeLabel, language, onPress, onHoverIn, onHoverOut }: RowProps) {
  const diet = dietKindOf(recipe);
  const rating = recipe.sourceRating && recipe.sourceRating > 0
    ? recipe.sourceRating.toLocaleString(language, { minimumFractionDigits: 1, maximumFractionDigits: 1 })
    : null;
  const expanded = hovered && !!placeLabel;

  return (
    <Pressable
      testID={`library-row-${recipe.id}`}
      accessibilityRole="button"
      accessibilityLabel={placeLabel ? `${title} — ${placeLabel}` : title}
      style={[styles.row, hovered && styles.rowHovered]}
      onPress={onPress}
      onHoverIn={onHoverIn}
      onHoverOut={onHoverOut}
    >
      <View style={styles.thumb}>
        <RemoteImage
          uri={recipe.image}
          variant="thumb"
          style={styles.thumbImage}
          resizeMode="cover"
          fallback={<RecipePlaceholder recipe={recipe} size={64} style={styles.thumbImage} />}
        />
      </View>
      <View style={styles.info}>
        <Text style={styles.title} numberOfLines={2}>{title}</Text>
        <View style={styles.meta}>
          <TimeChip minutes={recipe.readyInMinutes} />
          <View style={[styles.dietDot, { backgroundColor: PaprikaDietWash[diet].dot }]} />
          {!!rating && (
            <View style={styles.rating}>
              <Feather name="star" size={11} color={Paprika.textSecondary} />
              <Text style={styles.ratingText}>{rating}</Text>
            </View>
          )}
        </View>
      </View>
      {expanded ? (
        <LinearGradient
          colors={PaprikaGradients.cookCta as unknown as [string, string]}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={styles.placeButton}
        >
          <Feather name="plus" size={15} color={Paprika.onPrimary} />
          <Text style={styles.placeText}>{placeLabel}</Text>
        </LinearGradient>
      ) : (
        <View style={styles.addButton}>
          <Feather name="plus" size={16} color={Paprika.primary} />
        </View>
      )}
    </Pressable>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    minHeight: 0,
  },
  searchRow: {
    flexDirection: 'row',
    paddingHorizontal: 20,
    paddingBottom: 12,
  },
  tokensBlock: {
    paddingHorizontal: 20,
    paddingBottom: 12,
    gap: 8,
  },
  tokenRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  chips: {
    // SearchShortcutChips pads itself for the Recipes tab (18); nudge to the panel's 20.
    marginHorizontal: 2,
  },
  list: {
    flex: 1,
  },
  listContent: {
    paddingHorizontal: 10,
    paddingBottom: 24,
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    paddingHorizontal: 10,
    paddingTop: 14,
    paddingBottom: 6,
  },
  eyebrow: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 11,
    letterSpacing: 0.9,
    textTransform: 'uppercase',
    color: Paprika.textMuted,
  },
  count: {
    fontFamily: 'Inter-Regular',
    fontSize: 12,
    color: Paprika.textMuted,
  },
  banner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginHorizontal: 10,
    marginTop: 6,
    paddingHorizontal: 13,
    paddingVertical: 11,
    borderRadius: 12,
    backgroundColor: Paprika.surfaceLow,
  },
  bannerText: {
    flex: 1,
    fontFamily: 'Inter-Regular',
    fontSize: 13,
    color: Paprika.textSecondary,
  },
  empty: {
    alignItems: 'center',
    gap: 10,
    paddingVertical: 40,
    paddingHorizontal: 24,
  },
  emptyText: {
    fontFamily: 'Inter-Regular',
    fontSize: 14,
    color: Paprika.textSecondary,
    textAlign: 'center',
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
    paddingHorizontal: 10,
    paddingVertical: 8,
    borderRadius: 14,
  },
  rowHovered: {
    backgroundColor: Paprika.surfaceLow,
  },
  thumb: {
    width: 64,
    height: 64,
    borderRadius: 12,
    overflow: 'hidden',
    backgroundColor: Paprika.bgSunk,
  },
  thumbImage: {
    width: '100%' as any,
    height: '100%' as any,
  },
  info: {
    flex: 1,
    minWidth: 0,
    gap: 5,
  },
  title: {
    fontFamily: 'Newsreader-Italic',
    fontSize: 17,
    lineHeight: 21,
    color: Paprika.textPrimary,
  },
  meta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  dietDot: {
    width: 7,
    height: 7,
    borderRadius: 4,
  },
  rating: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
  },
  ratingText: {
    fontFamily: 'Inter-Regular',
    fontSize: 12,
    color: Paprika.textSecondary,
  },
  addButton: {
    width: 34,
    height: 34,
    borderRadius: 17,
    backgroundColor: Paprika.brandTint,
    justifyContent: 'center',
    alignItems: 'center',
  },
  placeButton: {
    height: 34,
    borderRadius: 17,
    paddingLeft: 11,
    paddingRight: 14,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  placeText: {
    fontFamily: 'Inter-Medium',
    fontSize: 13,
    color: Paprika.onPrimary,
  },
});
